import { useState } from 'react';
import { getPlanNumberLots } from '../api/suggestionService';

export const useBuildingLots = (formData, setFormData) => {
    const [buildingsData, setBuildingsData] = useState({ buildings: [] });
    const [selectedBuildings, setSelectedBuildings] = useState([]);
    const [expandedBuildings, setExpandedBuildings] = useState({});
    const [showBuildingDropdown, setShowBuildingDropdown] = useState(false);
    const [buildingSearchTerms, setBuildingSearchTerms] = useState({});
    const [planNumberLots, setPlanNumberLots] = useState([]);

    const getLotsByPlanNumber = async (planNumber, databaseName) => {
        try {
            const response = await getPlanNumberLots(planNumber, databaseName);
            setPlanNumberLots(response || []);
            loadBuildingsData(planNumber);
        } catch (error) {
            console.error('Error fetching lots by plan number:', error);
            setPlanNumberLots([]);
        }
    };
    
    const loadBuildingsData = async (planNumber) => {
        try {
            const buildingsDataModule = await import('../data/buildingsData.json');
            const data = buildingsDataModule.default;
            if (data[planNumber]) {
                setBuildingsData(data[planNumber]);
            } else {
                setBuildingsData({});
            }
            setSelectedBuildings([]);
            setExpandedBuildings({});
            setBuildingSearchTerms({});
            setShowBuildingDropdown(false);
        } catch (error) {
            console.error('Error loading buildings data:', error);
            setBuildingsData({});
            setSelectedBuildings([]);
            setExpandedBuildings({});
            setBuildingSearchTerms({});
            setShowBuildingDropdown(false);
        }
    };
    
    const handleBuildingSelection = (buildingId) => {
        setSelectedBuildings(prev => {
            const isSelected = prev.includes(buildingId);
            let newSelection;
            
            if (isSelected) {
                newSelection = prev.filter(id => id !== buildingId);
                const building = buildingsData.buildings?.find(b => b.id === buildingId);
                if (building) {
                    setFormData(prevData => ({
                        ...prevData,
                        selectedLots: prevData.selectedLots.filter(lot => !building.lots.includes(lot))
                    }));
                }
            } else {
                newSelection = [...prev, buildingId];
                const building = buildingsData.buildings?.find(b => b.id === buildingId);
                if (building) {
                    setFormData(prevData => ({
                        ...prevData,
                        selectedLots: [...new Set([...prevData.selectedLots, ...building.lots])]
                    }));
                }
            }
            
            return newSelection;
        });
    };
    
    const toggleBuildingExpansion = (buildingId) => {
        setExpandedBuildings(prev => {
            const isCurrentlyExpanded = prev[buildingId];
            if (isCurrentlyExpanded) {
                return {};
            } else {
                return { [buildingId]: true };
            }
        });
    };
    
    const handleBuildingSearch = (buildingId, searchTerm) => {
        setBuildingSearchTerms(prev => ({
            ...prev,
            [buildingId]: searchTerm
        }));
    };
    
    const getFilteredLots = (building) => {
        const searchTerm = buildingSearchTerms[building.id] || '';
        if (!searchTerm.trim()) {
            return building.lots;
        }
        return building.lots.filter(lot => 
            lot.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
    };
    
    const handleSelectAllBuilding = (buildingId) => {
        const building = buildingsData.buildings?.find(b => b.id === buildingId);
        if (!building) return;
        
        const allLotsSelected = building.lots.every(lot => formData.selectedLots.includes(lot));
        
        if (allLotsSelected) {
            setFormData(prevData => ({
                ...prevData,
                selectedLots: prevData.selectedLots.filter(lot => !building.lots.includes(lot))
            }));
            setSelectedBuildings(prev => prev.filter(id => id !== buildingId));
        } else {
            setFormData(prevData => ({
                ...prevData,
                selectedLots: [...new Set([...prevData.selectedLots, ...building.lots])]
            }));
            setSelectedBuildings(prev => prev.includes(buildingId) ? prev : [...prev, buildingId]);
        }
    };
    
    const updateBuildingSelectionsFromLots = (selectedLots) => {
        if (!buildingsData.buildings) return;
        
        const newSelectedBuildings = [];
        
        buildingsData.buildings.forEach(building => {
            const anyLotsSelected = building.lots.some(lot => selectedLots.includes(lot));
            
            if (anyLotsSelected) {
                newSelectedBuildings.push(building.id);
            }
        });
        
        setSelectedBuildings(newSelectedBuildings);
    };

    return {
        buildingsData,
        setBuildingsData,
        selectedBuildings,
        setSelectedBuildings,
        expandedBuildings,
        setExpandedBuildings,
        showBuildingDropdown,
        setShowBuildingDropdown,
        buildingSearchTerms,
        setBuildingSearchTerms,
        planNumberLots,
        setPlanNumberLots,
        getLotsByPlanNumber,
        loadBuildingsData,
        handleBuildingSelection,
        toggleBuildingExpansion,
        handleBuildingSearch,
        getFilteredLots,
        handleSelectAllBuilding,
        updateBuildingSelectionsFromLots
    };
};
